import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { Alert, AlertDescription } from './alert';
import { AlertTriangle, FileText, BookOpen, List, Link } from 'lucide-react';

interface RelatedDataInfo {
  hasRelatedData: boolean;
  relatedArticles: number;
  relatedOutlines: number;
  relatedSections: number;
  relatedCitations: number;
  details?: {
    articles: Array<{ id: string; title: string; status: string }>;
    outlines: Array<{ id: string; createdAt: string }>;
  };
}

interface DeletionConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  title: string;
  itemName: string;
  relatedData?: RelatedDataInfo;
  isLoading?: boolean;
}

export const DeletionConfirmationDialog: React.FC<DeletionConfirmationDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  title,
  itemName,
  relatedData,
  isLoading = false
}) => {
  const hasRelatedData = relatedData?.hasRelatedData;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete "{itemName}"?
            {hasRelatedData ? ' This action will affect related data.' : ' This action cannot be undone.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {hasRelatedData && relatedData && (
          <div className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This article brief has related data that will be preserved but may become inaccessible:
              </AlertDescription>
            </Alert>

            <div className="space-y-2 text-sm">
              {relatedData.relatedArticles > 0 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  <span>{relatedData.relatedArticles} article{relatedData.relatedArticles !== 1 ? 's' : ''}</span>
                </div>
              )}
              
              {relatedData.relatedOutlines > 0 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <BookOpen className="h-4 w-4" />
                  <span>{relatedData.relatedOutlines} outline{relatedData.relatedOutlines !== 1 ? 's' : ''}</span>
                </div>
              )}
              
              {relatedData.relatedSections > 0 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <List className="h-4 w-4" />
                  <span>{relatedData.relatedSections} section{relatedData.relatedSections !== 1 ? 's' : ''}</span>
                </div>
              )}
              
              {relatedData.relatedCitations > 0 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Link className="h-4 w-4" />
                  <span>{relatedData.relatedCitations} citation{relatedData.relatedCitations !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>

            {relatedData.details?.articles && relatedData.details.articles.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Related Articles:</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {relatedData.details.articles.map((article) => (
                    <div key={article.id} className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                      <div className="font-medium truncate">{article.title}</div>
                      <div className="text-xs">Status: {article.status}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};